import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import SammiLogo from '../components/common/SammiLogo';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Section from '../components/common/Section';
import LoginService from '../services/login.service';
import { OrganizationData, UserData } from '../types/Registration';

interface Address {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

export const RegisterAdminPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [organizationData, setOrganizationData] = useState<OrganizationData | null>(null);

  const [userData, setUserData] = useState<UserData>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'MANAGER'
  });

  useEffect(() => {
    // Get organization data from localStorage
    const pendingOrgData = localStorage.getItem('pendingOrganization');
    if (!pendingOrgData) {
      // If no organization data, redirect back to organization setup
      navigate('/register-org');
      return;
    }
    
    try {
      const orgData = JSON.parse(pendingOrgData);
      setOrganizationData(orgData);
    } catch (error) {
      console.error('Error parsing organization data:', error);
      navigate('/register-org');
    }
  }, [navigate]);

  const updateUser = (field: keyof UserData, value: string) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): boolean => {
    // Check required user fields
    if (!userData.firstName || !userData.lastName || !userData.phoneNumber || !userData.email || !userData.password) {
      setErrorMessage('Please fill in all required fields.');
      return false;
    }

    // Check password confirmation
    if (userData.password !== userData.confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      setErrorMessage('Please enter a valid email address.');
      return false;
    }

    // Password strength check
    if (userData.password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long.');
      return false;
    }

    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(userData.password)) {
      setErrorMessage('Password must contain at least one uppercase letter.');
      return false;
    }

    // Check for at least one lowercase letter
    if (!/[a-z]/.test(userData.password)) {
      setErrorMessage('Password must contain at least one lowercase letter.');
      return false;
    }

    // Check for at least one number
    if (!/\d/.test(userData.password)) {
      setErrorMessage('Password must contain at least one number.');
      return false;
    }

    // Check for at least one special character
    if (!/[^A-Za-z0-9]/.test(userData.password)) {
      setErrorMessage('Password must contain at least one special character.');
      return false;
    }

    setErrorMessage('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !organizationData) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Prepare the organization signup payload
      const organizationSignupData = {
        organizationName: organizationData.name,
        organizationPhoneNumber: organizationData.phoneNumber,
        organizationEmail: organizationData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phoneNumber: userData.phoneNumber,
        email: userData.email,
        password: userData.password,
        userType: userData.userType
      };

      // Make API call to create organization and user using service
      const result = await LoginService.organizationSignup(organizationSignupData);

      // Store the created organization and user data for payment step
      const createdData = {
        organization: {
          id: result.organization?._id,
          name: organizationData.name,
          email: organizationData.email,
          phoneNumber: organizationData.phoneNumber
        },
        user: {
          id: result.user?._id,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          phoneNumber: userData.phoneNumber,
          userType: userData.userType
        }
      };

      // Store the created data and setup token for payment step
      localStorage.setItem('createdOrganization', JSON.stringify(createdData.organization));
      localStorage.setItem('createdUser', JSON.stringify(createdData.user));
      
      // Store the setup token for payment method attachment (expires quickly)
      if (result.setupToken) {
        localStorage.setItem('paymentSetupToken', result.setupToken);
      } else {
        console.warn('No setup token received from server - payment step may fail');
      }
      
      // Clear the pending data since we've successfully created the org/user
      localStorage.removeItem('pendingOrganization');
      localStorage.removeItem('pendingUser');
      
      // Navigate to payment step
      navigate('/register-payment');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create organization and user. Please try again.';
      setErrorMessage(errorMessage);
      console.error('Organization signup error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/register-org');
  };

  if (!organizationData) {
    return null; // Loading or redirect
  }

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-start py-4 px-4 overflow-auto"
      style={{
        background: 'linear-gradient(135deg, rgba(25, 183, 223, 0.75) 0%, rgba(65, 239, 170, 0.75) 100%), linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}
    >
      <div className="flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-4 md:p-8 lg:p-12 w-full max-w-xl mx-auto my-auto">
        {/* Header */}
        <div className="flex items-center justify-between w-full mb-4 md:mb-6">
          <button
            onClick={handleBack}
            className="flex items-center transition-colors duration-200"
            style={{ color: '#002457' }}
            onMouseEnter={(e) => (e.currentTarget as HTMLButtonElement).style.color = '#41EFAA'}
            onMouseLeave={(e) => (e.currentTarget as HTMLButtonElement).style.color = '#002457'}
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back
          </button>
        </div>

        <SammiLogo />
        
        <h2 className="text-xl md:text-2xl font-bold mb-1 md:mb-2 text-center" style={{ color: '#002457' }}>
          Create Admin Account
        </h2>
        <p className="text-gray-600 mb-1 md:mb-2 text-center text-sm md:text-base">
          Step 2 of 3: Admin User Information
        </p>
        <p className="text-sm text-gray-500 mb-6 md:mb-8 text-center">
          For organization: <strong>{organizationData.name}</strong>
        </p>

        <form onSubmit={handleSubmit} className="w-full">
          <Section title="Admin User Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                id="firstName"
                label="First Name"
                value={userData.firstName}
                onChange={(value: string) => updateUser('firstName', value)}
                placeholder="John"
                required
                autoComplete="given-name"
              />
              
              <Input
                id="lastName"
                label="Last Name"
                value={userData.lastName}
                onChange={(value: string) => updateUser('lastName', value)}
                placeholder="Doe"
                required
                autoComplete="family-name"
              />
            </div>
            
            <Input
              id="userPhone"
              label="Phone Number"
              type="tel"
              value={userData.phoneNumber}
              onChange={(value: string) => updateUser('phoneNumber', value)}
              placeholder="(555) 123-4567"
              required
              autoComplete="tel"
              formatPhone
            />
            
            <Input
              id="userEmail"
              label="Email Address"
              type="email"
              value={userData.email}
              onChange={(value: string) => updateUser('email', value)}
              placeholder="john.doe@yourcompany.com"
              required
              autoComplete="username"
            />
            
            <Input
              id="password"
              label="Password"
              type="password"
              value={userData.password}
              onChange={(value: string) => updateUser('password', value)}
              placeholder="Create a strong password"
              required
              autoComplete="new-password"
            />
            
            <Input
              id="confirmPassword"
              label="Confirm Password"
              type="password"
              value={userData.confirmPassword}
              onChange={(value: string) => updateUser('confirmPassword', value)}
              placeholder="Confirm your password"
              required
              autoComplete="new-password"
            />
            
            <div className="text-xs text-gray-500 mt-2">
              Password must be at least 8 characters long and contain:
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>At least one uppercase letter</li>
                <li>At least one lowercase letter</li>
                <li>At least one number</li>
                <li>At least one special character</li>
              </ul>
            </div>
          </Section>

          {errorMessage && (
            <div className="mb-6 p-4 rounded-2xl bg-red-500 text-white text-center text-sm">
              {errorMessage}
            </div>
          )}

          <div className="flex flex-col space-y-4">
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating Organization...' : 'Create Organization & Continue'}
            </Button>
            
            <div className="text-center">
              <span className="text-gray-600 text-sm">Already have an account? </span>
              <Link 
                to="/login" 
                className="text-sm transition-colors duration-200"
                style={{ color: '#002457', textDecoration: 'none' }}
                onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.color = '#41EFAA'}
                onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.color = '#002457'}
              >
                Sign in here
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterAdminPage;
